import dayjs from 'dayjs';
import { FC } from 'react';
import { AiOutlineDelete } from 'react-icons/ai';
import { ActionsIcon } from '../../../../../../assets/svg/actions';
import { EyeIcon } from '../../../../../../assets/svg/eye';
import { IconButton } from '../../../../../../components/icon-button/icon-button';
import { InfiniteScroll } from '../../../../../../components/infinite-scroll/infinite-scroll';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { Tooltip } from '../../../../../../features/tooltip/tooltip';
import { formatRuDateString } from '../../../../../../helpers/date-time';
import { useLang } from '../../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { useCheckInCheckOutStore } from '../../../../../../store/use-check-in-check-out-store';
import { getDatesDiff } from '../check-in-check-out-helpers';
import s from './check-in-check-out-cards.module.css';

interface Props {
  onEnd: () => void;
}

export const CheckInCheckOutCards: FC<Props> = ({ onEnd }) => {
  const { checkins, isLoadingFetch } = useCheckInCheckOutStore();
  const { t, getLocale } = useLang();
  const { addQueryParams } = useQueryParams();
  const nestedCheckinsModal = useModalParams('nestedCheckins');

  if (!checkins?.length && !isLoadingFetch) {
    return <div>{t('Здесь пока нет приходов/уходов')}</div>;
  }

  return (
    <ul className={s.feature}>
      {checkins?.map((item) => {
        const employee = item.staff;
        const tags = employee.tags || [];
        const sessions = item.checkins || [];

        return (
          <li key={item.id} className={s.card}>
            <div className={s.title}>
              <div className={s.inUse}>
                {t('Дата')}
                <span>{item.start_time ? formatRuDateString(item.start_time) : '-'}</span>
              </div>

              <Tooltip
                triggerWrapperClassName={s.actionsButton}
                optionsWrapperClassName={s.actions}
                trigger={<ActionsIcon />}
                offset={12}
              >
                <div className={s.actions}>
                  {sessions.length > 1 && (
                    <IconButton
                      tooltip={t('Просмотр сессий')}
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        nestedCheckinsModal.handleOpenModal({ checkInId: String(item.id) })
                      }
                    >
                      <EyeIcon />
                    </IconButton>
                  )}
                  <HasAccess service="staff" permissions={['write']}>
                    <IconButton
                      tooltip={t('Удалить')}
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        addQueryParams({
                          deleteId: String(item.id),
                        })
                      }
                    >
                      <AiOutlineDelete />
                    </IconButton>
                  </HasAccess>
                </div>
              </Tooltip>
            </div>
            <div className={s.checkin}>
              <span className={s.pair}>
                {t('Приход')}:{' '}
                <span>{item.start_time ? dayjs(item.start_time).format('HH:mm') : '-'}</span>
              </span>
              <span className={s.pair}>
                {t('Уход')}:{' '}
                <span>{item.end_time ? dayjs(item.end_time).format('HH:mm') : '-'}</span>
              </span>
              {sessions.length > 1 && (
                <span className={s.pair}>
                  {t('Сессий')}: <span>{sessions.length}</span>
                </span>
              )}
            </div>
            <span className={s.name}>{`${getLocale(employee.name)} ${getLocale(
              employee.surname,
            )}`}</span>

            <div className={s.footer}>
              <div className={s.col}>
                {t('Должности')}
                <span>
                  {tags.length > 1 ? `${tags[0].value}, +${tags.length - 1}` : tags?.[0]?.value}
                </span>
              </div>
              <div className={s.col}>
                {t('Факт. время')}
                <span>{getDatesDiff(item.start_time, item.end_time) || '-'}</span>
              </div>
            </div>
          </li>
        );
      })}
      <InfiniteScroll onEnterView={onEnd} />
    </ul>
  );
};
